import React from 'react'

import { ObjectHash } from '../../../types/shared'
import { CanvasElementType } from '../../constants/common'
import ChartLayoutModel from '../../models/chart_layout'
import Quote from '../../models/quote'
import { GlobalState, useGlobalState } from '../../utils/global-state'

interface ChartLayoutModelGlobalState {
  chartLayoutModel: ChartLayoutModel | null
  chartLayoutModelConfigSnapshot: ObjectHash | null
  chartLayoutModelConfigSnapshotToCompare: ObjectHash | null
  lastGlobalRerender: number
}

export const chartLayoutGlobalState = new GlobalState<ChartLayoutModelGlobalState>({
  chartLayoutModel: null,
  chartLayoutModelConfigSnapshot: null,
  chartLayoutModelConfigSnapshotToCompare: null,
  lastGlobalRerender: Date.now(),
})

const CONFIG_PROPS_TO_OMIT = ['width', 'height', 'leftOffset', 'zoomFactor']
const CANVAS_ELEMENT_TYPES: string[] = Object.values(CanvasElementType)

function getConfigSnapshotToCompare(chartLayoutModel: ChartLayoutModel | null) {
  if (!chartLayoutModel) {
    return null
  }
  const config = chartLayoutModel.toConfig(CONFIG_PROPS_TO_OMIT)
  return {
    ...config,
    charts: config.charts.map((chart) => ({
      ...chart,
      panes: chart.panes.map((pane) => ({
        ...pane,
        elements: pane.elements.filter(({ type }) => !CANVAS_ELEMENT_TYPES.includes(type)),
      })),
    })),
  }
}

export function useChartLayoutGlobalModel() {
  const [state, setState] = useGlobalState(chartLayoutGlobalState)
  const getIsConfigChanged = () =>
    JSON.stringify(state.chartLayoutModelConfigSnapshotToCompare) !==
    JSON.stringify(getConfigSnapshotToCompare(state.chartLayoutModel))
  const getIsConfigChangedRef = React.useRef(getIsConfigChanged)

  getIsConfigChangedRef.current = getIsConfigChanged

  return React.useMemo(
    () => ({
      ...state,
      getIsConfigChanged: () => getIsConfigChangedRef.current(),
      setChartLayoutModel: (chartLayoutModel: ChartLayoutModel) => {
        setState({
          chartLayoutModel,
          chartLayoutModelConfigSnapshot: chartLayoutModel.toConfig(),
          chartLayoutModelConfigSnapshotToCompare: getConfigSnapshotToCompare(chartLayoutModel),
        })
      },
      generateNewLayoutModelConfigSnapshot: () => {
        if (state.chartLayoutModel) {
          setState({
            chartLayoutModelConfigSnapshot: state.chartLayoutModel.toConfig(),
            chartLayoutModelConfigSnapshotToCompare: getConfigSnapshotToCompare(state.chartLayoutModel),
          })
        }
      },
      getChartLayoutModelConfig: ({ fallback }: { fallback: ObjectHash }) =>
        state.chartLayoutModelConfigSnapshot ?? fallback,
      forceGlobalRerender: () => {
        Quote.deleteAll()
        /*
         * We don't want to override chartLayoutModelConfigSnapshot to null
         * because it is most likely used to build new chart layout model
         * */
        setState({
          lastGlobalRerender: Date.now(),
          chartLayoutModel: null,
          chartLayoutModelConfigSnapshotToCompare: null,
        })
      },
    }),
    [state, setState]
  )
}
